import {useMemo} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import CoCGraphViewer from '../CoCGraphViewer/cocGraphViewer';
import {statusIcons} from '../../../../../assets/svg';
import CoCAttachmentButton from '../CoCAttachmentButton/cocAttachmentButton';
import {validateFileType, getCoCAttachmentAcceptTypes} from '../../utils/cocHelper';
import useLabels from 'infrastructure/js/hooks/useLabels';
import DragAndDropArea from 'infrastructure/js/components/DragAndDropArea/dragAndDropArea';
import PermissionManager from 'infrastructure/js/utils/permissionManager';

import './cocAttachments.scss';

const MAX_GENERIC_ATTACHMENTS = 50;

export default function CoCAttachments({
                                         index,
                                         name,
                                         isViewOnly = false,
                                         attachmentsCounters = [],
                                         attachmentsFiles = [],
                                         onAttachmentUpload = null,
                                         onAttachmentUploadValidation = null,
                                         onAttachmentRemove = null,
                                         onAttachmentClick = null,
                                         onAttachmentDownload = null,
                                         onAttachmentResultChange = null,
                                         genericAttachmentsRequired = false,
                                         showEmptyFieldErrors = false,

                                       }) {

  const labels = useLabels('mat.forms.cocTestForm.sectionListItem.attachments.');

  // Computed once per relevant prop change instead of on every dragover/dragenter event.
  const isZoneDisabled = useMemo(() => {
    if (isViewOnly) {
      return true;
    }
    if (genericAttachmentsRequired) {
      return attachmentsFiles.filter(f => f.type === 'GENERIC').length >= MAX_GENERIC_ATTACHMENTS;
    }
    return attachmentsCounters.every(item => item.attachmentsTotal && item.attachmentsCount >= item.attachmentsTotal);
  }, [isViewOnly, genericAttachmentsRequired, attachmentsFiles, attachmentsCounters]);


  const getFileExtension = (fileName = '') => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex < 0) {
      return '';
    }
    return fileName.slice(lastDotIndex).toLowerCase();
  }

  // GRAPH and PDF attachments both accept .pdf files. GRAPH counters must be filled
  // to their max before falling back to the PDF counter for the same dropped file.
  const getOrderedCountersForDroppedFile = (file) => {
    const extension = getFileExtension(file.name);
    const matchingCounters = attachmentsCounters.filter(item => getCoCAttachmentAcceptTypes(item.type)?.split(',').includes(extension));

    return [...matchingCounters].sort((a, b) => {
      if (a.type === 'GRAPH') {
        return -1;
      }
      if (b.type === 'GRAPH') {
        return 1;
      }
      return 0;
    });
  }

  const processDroppedFiles = (e) => {
    if (isViewOnly) {
      return;
    }

    const files = e.dataTransfer?.files;
    const fileList = Array.from(files || []);

    if (genericAttachmentsRequired) {
      let attachedGenericsCount = attachmentsFiles.filter(f => f.type === 'GENERIC').length;
      fileList.forEach((file) => {
        if (attachedGenericsCount >= MAX_GENERIC_ATTACHMENTS) {
          return;
        }
        onAttachmentChanged(file, 'GENERIC');
        attachedGenericsCount++;
      });
      return;
    }

    const countersProgress = {};

    fileList.forEach((file) => {
      const orderedCounters = getOrderedCountersForDroppedFile(file);
      if (!orderedCounters.length) {
        onAttachmentUploadValidation?.({ type: 'warning',
          title: labels.get('invalidFileType.title'),
          message: labels.get('invalidFileType.message'),
        });
        return;
      }

      const targetCounter = orderedCounters.find(item => {
        const currentCount = countersProgress[item.type] ?? item.attachmentsCount;
        return !item.attachmentsTotal || currentCount < item.attachmentsTotal;
      });

      if (!targetCounter) {
        return;
      }

      countersProgress[targetCounter.type] = (countersProgress[targetCounter.type] ?? targetCounter.attachmentsCount) + 1;
      onAttachmentChanged(file, targetCounter.type);
    });
  }

  const onAttachmentChanged = (file, type) => {
    if (!validateFileType(file, type)) {
      return onAttachmentUploadValidation?.({ type: 'warning',
        title: labels.get('invalidFileType.title'),
        message: labels.get('invalidFileType.message'),
      });
    }
    const exists = attachmentsFiles.some(f => f.name === file.name);
    if (exists) {
      return onAttachmentUploadValidation?.(
        { type: 'warning',
          title: labels.get('duplicatedFile.title'),
          message: labels.get('duplicatedFile.message', '', {fileName: file.name}),
        });
    }

    const maxFileSize = PermissionManager.getOrgPreferences().attachmentsMaxFileSize;
    if (file.size >= maxFileSize * 1024) {
      return onAttachmentUploadValidation?.({ type: 'warning',
        title: labels.get('invalidFileSize.title'),
        message: labels.get('invalidFileSize.message', '', {maxSize: maxFileSize} ),
      });
    }

    onAttachmentUpload?.(file, type);
  }

  const renderRemoveFileButton = (item)  => {
    return isViewOnly ? null :
      (
        <span className="pl pl-x-input-close remove-attached-file-btn"
              onClick={(e) => onFileRemoveHandler(e, item)}/>
      );
  }

  const onFileRemoveHandler = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    if (attachmentsFiles.some(it => it.id === item.id)) {
      onAttachmentRemove?.(item);
    }
  }

  const renderDragAndDropZone = () => {
    if (!genericAttachmentsRequired && !attachmentsCounters.length) {
      return null;
    }

    return (
      <DragAndDropArea className={cn('attach-drag-drop-zone', {disabled: isZoneDisabled})}
                       disabled={isZoneDisabled}
                       onDropHandler={processDroppedFiles}
      >
        <div className="drag-drop-zone-hint">
          <span className="attach-drag-drop-zone-icon pl pl-file"/>
          <span className="drag-drop-label">{labels.get('dragAndDrop.label')}</span>
        </div>

        {genericAttachmentsRequired ? renderGenericButton() : renderSpecificButtons()}

      </DragAndDropArea>
    )
  }

  const renderGenericButton = () => {
    const attachedGenerics = attachmentsFiles.filter(f => f.type === 'GENERIC');
    const isGenericDisabled = isViewOnly || attachedGenerics.length >= MAX_GENERIC_ATTACHMENTS;

    return (
      <div className="attach-buttons-section">
        <span>{labels.get('attachGenericFiles')}</span>
        <CoCAttachmentButton key={`attach-btn-generic-${index}`}
                             id={`attach-btn-generic-${index}`}
                             type={'GENERIC'}
                             tooltip={''}
                             buttonLabel={labels.get('attachButton.label')}
                             disabled={isGenericDisabled}
                             onAttachmentChanged={onAttachmentChanged}
                             emptyError={showEmptyFieldErrors && attachedGenerics.length === 0}
        />
      </div>
    )
  }

  const renderSpecificButtons = () => {
    if (!attachmentsCounters.length) {
      return null;
    }

    return (
      <div className="attach-buttons-section">
        <span>{labels.get('attachFiles')}</span>
        <div className='attach-buttons'>
          {attachmentsCounters.map((item) =>
            <CoCAttachmentButton key={item.type}
                                 id={`attach-btn-${item.type}-${index}`}
                                 {...item}
                                 disabled={isViewOnly}
                                 onAttachmentChanged={onAttachmentChanged}
                                 emptyError={showEmptyFieldErrors && item.attachmentsCount < item.attachmentsTotal}
            /> )}
        </div>
      </div>
    )
  }

  const renderAttachedFilesSection = () => {
    const attachedFiles = genericAttachmentsRequired ?
      attachmentsFiles.filter(f => f.type === 'GENERIC') :
      attachmentsFiles.filter(f => f.type !== 'GRAPH');

    if (!attachedFiles.length) {
      return null;
    }

    return (
      <div className="attached-files-section">
        {attachedFiles.map((item, itemIndex) =>
          <div key={`${itemIndex}-${item.id}`} className='attached-file' onClick={() => onAttachmentDownload?.(item)}>
            <span className='pl pl-file'/>
            <span className='file-name'>{item.name || item.file?.name}</span>
            {renderRemoveFileButton(item)}
          </div>
        )}
      </div>
    )
  }

  const renderAttachedGraphsSection = () => {
    if (genericAttachmentsRequired ) {
      return null;
    }

    const attachedGraphs = attachmentsFiles.filter(f => f.type === 'GRAPH');
    if (!attachedGraphs.length) {
      return null;
    }

    return (
      <div className="attached-graphs-section">
        {attachedGraphs.map((item, itemIndex) => {
          const isEmptyResult = showEmptyFieldErrors && (!item.testResult || item.testResult === 'N/A');
          return (
            <div key={`${itemIndex}-${item.id}`} className='attached-graph-item'>
              <div className={cn('attached-graph-file', {'empty-error': isEmptyResult})}>
                <div className={item.isExpanded ? 'pl pl-arrow-down' : 'pl pl-arrow-right'} onClick={() => onAttachmentClick?.(item)}/>
                <div className='attached-file' onClick={() => onAttachmentDownload?.(item)}>
                  <span className='pl pl-file'/>
                  <span className='file-name'>{item.name || item.file?.name}</span>
                  {renderRemoveFileButton(item)}
                </div>
                {item.testResult === 'PASS' ? <statusIcons.statusPass/> : (item.testResult === 'FAIL' ? <statusIcons.statusFail/> : null)}
              </div>

              {item.isExpanded &&
                <CoCGraphViewer graph={item} index={itemIndex}
                                name={name}
                                isViewOnly={isViewOnly}
                                onGraphResultChange={onAttachmentResultChange}
                                showEmptyFieldErrors={showEmptyFieldErrors}
                />}
            </div>
          );
        })}
      </div>
    )
  }

  return (
    <div className="coc-attachments">
      {renderDragAndDropZone()}
      {renderAttachedFilesSection()}
      {renderAttachedGraphsSection()}
    </div>
  )
}

CoCAttachments.propTypes = {
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  isViewOnly: PropTypes.bool,
  attachmentsCounters: PropTypes.array,
  attachmentsFiles: PropTypes.array,
  onAttachmentUpload: PropTypes.func,
  onAttachmentUploadValidation: PropTypes.func,
  onAttachmentRemove: PropTypes.func,
  onAttachmentClick: PropTypes.func,
  onAttachmentDownload: PropTypes.func,
  onAttachmentResultChange: PropTypes.func,
  genericAttachmentsRequired: PropTypes.bool,
  showEmptyFieldErrors: PropTypes.bool,
}
